// @flow

import React, { Component } from 'react'
import Page from './Page'
import { isEmpty } from 'lodash'
import WebContent from './WebContent'
import VideoContent from './VideoContent'
import PDFContent from './PDFContent'
import RichTextContent from './RichTextContent'
import IFrameContent from './IFrameContent'
import NotFound from './NotFound'
import { createGlobalStyle } from 'styled-components'
import color from 'michelangelo/dist/Components/styles/color'

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
  }

  canvas {
    border: 1px solid ${color.lightDisabled};
  }

`
type ContentProps = {
  content: Object,
  url: string,
  host: string
}
type ContentState = {
  loaded: boolean
}
const ContentTypes = {
  CONTENT_WEB: 'CONTENT_WEB',
  CONTENT_RICH_TEXT: 'CONTENT_RICH_TEXT',
  CONTENT_PDF: 'CONTENT_PDF',
  CONTENT_VIDEO: 'CONTENT_VIDEO',
  CONTENT_IMAGE: 'CONTENT_IMAGE',
  CONTENT_AUDIO: 'CONTENT_AUDIO',
  CONTENT_DOCX: 'CONTENT_DOCX',
  CONTENT_PPTX: 'CONTENT_PPTX',
  CONTENT_XLSX: 'CONTENT_XLSX'
}

class Content extends Component<ContentProps, ContentState> {
  UNSAFE_componentWillMount () {
    this.setState({ loaded: false })
  }

  componentDidMount () {
    this.setState({ loaded: true })
  }

  coalesceImageUrls (image: ?string, url: ?string): ?string {
    if (!image && url) {
      return url
    } else {
      return image
    }
  }

  getImageFile () {
    const { content } = this.props
    if (isEmpty(content)) {
      return ''
    }
    if (content.contentType === ContentTypes.CONTENT_IMAGE) {
      return content.fileUrl
    }
    let image: ?string
    if (content.displayThumbnail) {
      image = this.coalesceImageUrls(image, content.xlargeThumbnailUrl)
      image = this.coalesceImageUrls(image, content.largeThumbnailUrl)
      image = this.coalesceImageUrls(image, content.mediumThumbnailUrl)
      image = this.coalesceImageUrls(image, content.smallThumbnailUrl)
      image = this.coalesceImageUrls(image, content.signedThumbnailUrl)
    }
    return image || ''
  }

  formatCaption (content) {
    const caption = content.caption || ''
    const matches = caption.match(/@[a-z\d]{24}/g) || []

    const mentions = content.mentions || []

    let newCaption = caption
    if (matches != null) {
      for (const match of matches) {
        const userId = match.substr(1)
        const user = mentions.find((user) => user._id.toString() === userId.toString())
        if (user !== null && user !== undefined) {
          newCaption = newCaption.replace(match, '[' + user.name + '](' + userId + ')')
        } else {
          newCaption = newCaption.replace(match, '[Deleted User]()')
        }
      }
    }
    return newCaption
  }

  getWebContent () {
    const { content } = this.props
    const image = this.getImageFile()

    const caption = this.formatCaption(content)
    return (
      <WebContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        webUrl={content.webUrl}
        thumbnail={image}
        contentType={content.contentType}
        hashtags={content.hashtags}
      />
    )
  }

  getVideoContent (isAudio: boolean) {
    const { content } = this.props
    const fileUrl = content.fileUrl
    const hlsVideoPlaylistUrl = content.hlsVideoPlaylistUrl ? content.hlsVideoPlaylistUrl : ''

    const caption = this.formatCaption(content)

    return (
      <VideoContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        thumbnail={this.getImageFile()}
        fileUrl={fileUrl}
        hlsVideoPlaylistUrl={hlsVideoPlaylistUrl}
        isAudio={isAudio}
        hashtags={content.hashtags}
      />
    )
  }

  getPDFContent () {
    const { content } = this.props

    const caption = this.formatCaption(content)

    return (
      <PDFContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        fileUrl={content.fileUrl}
        pdfS3Key={content.pdfS3Key}
        thumbnail={this.getImageFile()}
        hashtags={content.hashtags}
      />
    )
  }

  getIframeContent () {
    const { content } = this.props

    const caption = this.formatCaption(content)

    return (
      <IFrameContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        url={content.htmlS3Key}
        thumbnail={this.getImageFile()}
        hashtags={content.hashtags}
      />
    )
  }

  getRichTextContent () {
    const { content } = this.props

    const caption = this.formatCaption(content)

    return (
      <RichTextContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        html={content.html}
        thumbnail={content.signedThumbnailUrl}
        hashtags={content.hashtags}
      />
    )
  }

  getImageContent () {
    const { content } = this.props

    const caption = this.formatCaption(content)

    return (
      <WebContent
        title={content.title}
        logoUrl={content.companyLogoUrl || ''}
        caption={caption}
        html={content.html}
        fileUrl={content.fileUrl}
        contentType={content.contentType}
        hashtags={content.hashtags}
      />
    )
  }

  getDefaultContent () {
    return <NotFound />
  }

  getPageContent () {
    const { content } = this.props
    const { loaded } = this.state
    if (!loaded) {
      return null
    }
    switch (content.contentType) {
      case ContentTypes.CONTENT_WEB:
        return this.getWebContent()
      case ContentTypes.CONTENT_VIDEO:
        return this.getVideoContent(false)
      case ContentTypes.CONTENT_AUDIO:
        return this.getVideoContent(true)
      case ContentTypes.CONTENT_PDF:
        return this.getPDFContent()
      case ContentTypes.CONTENT_RICH_TEXT:
        return this.getRichTextContent()
      case ContentTypes.CONTENT_IMAGE:
        return this.getImageContent()
      case ContentTypes.CONTENT_DOCX:
      case ContentTypes.CONTENT_PPTX:
      case ContentTypes.CONTENT_XLSX:
        if (content.pdfS3Key) {
          return this.getPDFContent()
        }
        return this.getIframeContent()
      default:
        return this.getDefaultContent()
    }
  }

  getBodyContainer () {
    const { content, host, url } = this.props
    const empty = isEmpty(content)

    return (
      <Page
        id='content'
        title={empty ? 'Page not found' : content.title}
        image={empty ? null : this.getImageFile()}
        description={empty ? 'The page you requested could not be found!' : content.caption}
        url={url}
        host={host}
        noCrawl={false}
        published={empty ? null : content.created}
      >
        <GlobalStyle />
        {empty ? <NotFound /> : this.getPageContent()}
      </Page>
    )
  }

  render () {
    const bodyContainer = this.getBodyContainer()

    return bodyContainer
  }
}

export default Content
